/* TWD COLLECTION COUNT SNAPSHOTS - 2026-09-06
 * Counts come from the same Liquid card-selection snippet as the storefront.
 * The utility view renders no card interiors. It NEVER inserts cards or controls
 * pagination. Native Shopify pagination remains the sole grid loader.
 */
(() => {
  'use strict';
  if (window.TWDCollectionCounts?.version === 3) return;
  const VERSION = 2; // Count-view protocol: existing snapshots remain compatible.
  const ENGINE_VERSION = 3;
  const PRODUCT_TYPE_PARAM = 'filter.p.product_type';
  // TWD PRODUCT TYPE COUNT FIX - 2026-09-06
  // Header, footer, Color and Product Type all count eligible display cards.
  // Product type buckets use every source page, never just the currently loaded DOM.
  const CACHE_MS = 60000;
  const MAX_CACHE = 32;
  const MAX_PAGES = 500;
  const cache = new Map();
  const flights = new Map();
  const waiting = [];
  let running = 0;
  let generation = 0;
  let active = null;

  function abortError() { return new DOMException('Count request superseded', 'AbortError'); }
  function check(signal) { if (signal?.aborted) throw abortError(); }
  function sameOrigin(url) {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) throw new Error('Count endpoint must be same-origin.');
    return parsed;
  }
  function normalizedURL(url) {
    const result = sameOrigin(url);
    result.hash = '';
    for (const key of Array.from(result.searchParams.keys())) {
      if (key === 'page' || key.startsWith('page_') || key === 'section_id' || key === 'sections' || key === 'sections[]') {
        result.searchParams.delete(key);
      }
    }
    result.searchParams.delete('view');
    result.searchParams.sort();
    return result;
  }
  function snapshot({ scope = 'current' } = {}) {
    const ctx = document.querySelector('[data-twd-count-context="2"]');
    if (!ctx || !ctx.dataset.collectionId) return null;
    const url = normalizedURL(window.location.href);
    if (scope === 'collection') {
      for (const key of Array.from(url.searchParams.keys())) {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      }
      // q defines the resource itself on /collections/types and /collections/vendors.
      if (!/\/collections\/(types|vendors)\/?$/.test(url.pathname)) url.searchParams.delete('q');
    }
    const value = {
      url: url.href,
      collectionId: ctx.dataset.collectionId,
      separate: ctx.dataset.separateColor === 'true',
      saleFamily: ctx.dataset.saleFamily === 'true',
      sectionId: ctx.dataset.sectionId,
      scope,
    };
    value.key = `${value.url}|${value.collectionId}|${value.separate}|${value.saleFamily}`;
    return value;
  }
  function pump() {
    while (running < 2 && waiting.length) {
      const item = waiting.shift();
      running += 1;
      Promise.resolve().then(() => { check(item.signal); return item.task(); })
        .then(item.resolve, item.reject)
        .finally(() => { running -= 1; pump(); });
    }
  }
  function limited(task, signal) {
    return new Promise((resolve, reject) => {
      waiting.push({ task, signal, resolve, reject });
      pump();
    });
  }
  async function fetchText(url, signal) {
    return limited(async () => {
      const controller = new AbortController();
      const relay = () => controller.abort();
      signal?.addEventListener('abort', relay, { once: true });
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        check(signal);
        const response = await fetch(url, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Count endpoint returned HTTP ${response.status}.`);
        return await response.text();
      } catch (error) {
        if (!signal?.aborted && controller.signal.aborted) throw new Error('Count endpoint timed out.');
        throw error;
      } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', relay);
      }
    }, signal);
  }
  function int(value, name) {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error(`Invalid count field: ${name}.`);
    return value;
  }
  function parsePage(text, expectedPage, snap) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const root = doc.querySelector('[data-twd-count-page="2"]');
    const script = root?.querySelector('[data-twd-count-meta]');
    if (!root || !script) throw new Error('Count view missing. Check that all patch files are installed on the preview theme.');
    const meta = JSON.parse(script.textContent);
    if (meta.version !== VERSION || String(meta.collectionId) !== snap.collectionId || meta.separateColor !== snap.separate || meta.saleFamily !== snap.saleFamily) {
      throw new Error('Count response does not match the current collection/theme context.');
    }
    ['parentProducts', 'allParentProducts', 'page', 'pages', 'pageSize'].forEach(name => int(meta[name], name));
    if (meta.page !== expectedPage || meta.pageSize < 1 || meta.pages > MAX_PAGES) throw new Error('Invalid count pagination metadata.');
    const expectedPages = Math.ceil(meta.parentProducts / meta.pageSize);
    if (meta.pages !== expectedPages && !(meta.parentProducts === 0 && meta.pages === 1)) throw new Error('Source product count and page count disagree.');
    const markers = Array.from(root.querySelectorAll('template[data-twd-product-audit="true"]'));
    const expectedMarkers = Math.max(0, Math.min(meta.pageSize, meta.parentProducts - (meta.page - 1) * meta.pageSize));
    if (markers.length !== expectedMarkers) throw new Error('Incomplete count page; refusing to publish a partial total.');
    return { root, meta, markers };
  }
  function accumulate(page, acc) {
    for (const marker of page.markers) {
      const data = marker.dataset;
      if (!data.twdProductId || acc.products.has(data.twdProductId)) throw new Error('Duplicate/missing source product while counting; retry the audit.');
      const candidateColors = JSON.parse(data.twdCandidateColors || '[]');
      if (!Array.isArray(candidateColors)) throw new Error('Invalid candidate color data.');
      const available = data.twdClearanceDataAvailable === 'true';
      acc.products.set(data.twdProductId, {
        title: data.twdProductTitle || '',
        productType: Object.prototype.hasOwnProperty.call(data, 'twdProductType') ? data.twdProductType : null,
        variants: Number(data.twdTotalVariantCount || 0),
        flagged: available ? Number(data.twdFlaggedVariantCount || 0) : null,
        allowed: available ? Number(data.twdAllowedVariantCount || 0) : null,
        beforeCards: Number(data.twdBeforeCardCount || (candidateColors.length || 1)),
        candidateColors,
      });
      if (Object.prototype.hasOwnProperty.call(data, 'twdProductType')) {
        acc.rawTypes.set(data.twdProductType, (acc.rawTypes.get(data.twdProductType) || 0) + 1);
      }
      for (const color of new Set(candidateColors)) {
        acc.rawColors.set(color, (acc.rawColors.get(color) || 0) + 1);
      }
    }
    for (const card of page.root.querySelectorAll('.grid-item[data-twd-product-card="true"]')) {
      const id = card.dataset.twdProductId;
      if (!id || !acc.products.has(id)) throw new Error('Card has no corresponding source-product marker.');
      // Raw option text avoids handleize collisions such as "A B" and "A-B".
      const color = card.dataset.twdColorValue ?? '';
      const key = JSON.stringify([id, color]);
      if (acc.cards.has(key)) throw new Error('Duplicate product/color card in count snapshot.');
      acc.cards.add(key);
      acc.represented.add(id);
      const productType = acc.products.get(id).productType;
      if (productType !== null) acc.types.set(productType, (acc.types.get(productType) || 0) + 1);
      if (color) acc.colors.set(color, (acc.colors.get(color) || 0) + 1);
    }
  }
  function fingerprint(meta) {
    return JSON.stringify([meta.collectionId, meta.parentProducts, meta.allParentProducts, meta.pages, meta.separateColor, meta.saleFamily]);
  }
  async function scan(snap, queryURL, { signal, force = false } = {}) {
    const url = normalizedURL(queryURL);
    const key = `${url.href}|${snap.collectionId}|${snap.separate}|${snap.saleFamily}`;
    const saved = cache.get(key);
    if (!force && saved && Date.now() - saved.time < CACHE_MS) return saved.result;
    const inProgress = flights.get(key);
    if (!force && inProgress && inProgress.signal === signal) return inProgress.promise;
    const promise = (async () => {
      check(signal);
      const acc = { products: new Map(), cards: new Set(), represented: new Set(), colors: new Map(), rawColors: new Map(), types: new Map(), rawTypes: new Map() };
      let first;
      let pagesScanned = 0;
      let pageParam = 'page';
      for (let number = 1; number <= (first ? Math.max(1, first.pages) : 1); number += 1) {
        check(signal);
        const requestURL = new URL(url.href);
        requestURL.searchParams.set('view', 'twd-counts');
        requestURL.searchParams.set(pageParam, String(number));
        const page = parsePage(await fetchText(requestURL.href, signal), number, snap);
        if (!first) {
          first = page.meta;
          pageParam = first.pageParam || 'page';
        } else if (fingerprint(first) !== fingerprint(page.meta)) {
          throw new Error('Collection changed during count scan. Retry to obtain a consistent snapshot.');
        }
        accumulate(page, acc);
        pagesScanned += 1;
      }
      if (acc.products.size !== first.parentProducts) throw new Error('Not all source products were counted.');
      const productRows = Array.from(acc.products.values());
      const truncatedVariants = productRows.some(row => row.variants >= 250);
      const flagsKnown = !truncatedVariants && productRows.every(row => row.flagged !== null);
      const result = {
        complete: true,
        url: url.href,
        generatedAt: new Date().toISOString(),
        pagesScanned,
        shopifyParentProducts: first.parentProducts,
        shopifyAllParentProducts: first.allParentProducts,
        renderedProductCards: acc.cards.size,
        representedParentProducts: acc.represented.size,
        hiddenParentProductsInCurrentResult: first.parentProducts - acc.represented.size,
        cardsBeforeVisibilityRule: productRows.reduce((total, row) => total + row.beforeCards, 0),
        productsWithIsClearanceItemTrue: flagsKnown ? productRows.filter(row => row.flagged > 0).length : null,
        isClearanceItemTrueVariants: flagsKnown ? productRows.reduce((total, row) => total + row.flagged, 0) : null,
        clearanceAuditComplete: flagsKnown,
        variantAuditPossiblyTruncated: truncatedVariants,
        separateColor: first.separateColor,
        saleFamily: first.saleFamily,
        colorCounts: Object.fromEntries(acc.colors),
        rawColorProductCounts: Object.fromEntries(acc.rawColors),
        productTypeCounts: Object.fromEntries(acc.types),
        rawProductTypeCounts: Object.fromEntries(acc.rawTypes),
        productTypeDataComplete: productRows.every(row => row.productType !== null),
        filters: first.filters || [],
      };
      check(signal);
      cache.set(key, { time: Date.now(), result });
      while (cache.size > MAX_CACHE) cache.delete(cache.keys().next().value);
      return result;
    })();
    const record = { signal, promise };
    flights.set(key, record);
    try { return await promise; }
    finally { if (flights.get(key) === record) flights.delete(key); }
  }
  function loadedCards() {
    const cards = document.querySelectorAll('#items-grid > .grid-item[data-twd-product-card="true"]');
    return new Set(Array.from(cards, card => JSON.stringify([card.dataset.twdProductId, card.dataset.twdColorValue ?? card.dataset.twdColorKey ?? '']))).size;
  }
  function setHeader(text, status) {
    document.querySelectorAll('#ProductCount_header, #ProductCountDrawer, #ProductCount').forEach(node => {
      node.textContent = text;
      node.classList.remove('loading');
      node.dataset.twdCountStatus = status;
      node.setAttribute('aria-live', 'polite');
      node.setAttribute('aria-busy', status === 'pending' ? 'true' : 'false');
    });
  }
  function footer(result) {
    const loaded = loadedCards();
    for (const node of document.querySelectorAll('[data-twd-display-count-footer]')) {
      node.textContent = result && loaded <= result.renderedProductCards
        ? `Showing ${loaded}/${result.renderedProductCards}`
        : `${loaded} products loaded`;
    }
  }
  function colorInputs() { return Array.from(document.querySelectorAll('input[data-twd-color-filter="true"]')); }
  function countInputs() {
    return Array.from(document.querySelectorAll(`input[data-twd-color-filter="true"], input[name="${PRODUCT_TYPE_PARAM}"]`));
  }
  function countKey(input) { return JSON.stringify([input.name, input.value]); }
  function bucketCount(buckets, value) {
    return Object.prototype.hasOwnProperty.call(buckets, value) ? buckets[value] : 0;
  }
  function setFilterCount(input, value, status) {
    const caption = input.closest('label')?.querySelector('span.p-break-words');
    if (!caption) return;
    // Saved labels prevent translated/grouped display text being replaced by URL values.
    const label = input.dataset.twdFilterLabel || input.dataset.twdColorLabel || input.value;
    const suffix = status === 'pending' ? ' (...)' : status === 'exact' ? ` (${value})` : '';
    caption.textContent = label + suffix;
    caption.dataset.twdCountStatus = status;
    caption.title = status === 'error' ? 'Count could not be verified. Filter is still usable.' : '';
  }
  function applySession(session) {
    if (active !== session) return;
    if (session.result) {
      const count = session.result.renderedProductCards;
      setHeader(`${count} ${count === 1 ? 'product' : 'products'}`, 'exact');
    } else if (session.error) {
      setHeader(`${loadedCards()} products loaded`, 'error');
    } else setHeader('Counting...', 'pending');
    footer(session.result);
    for (const input of countInputs()) {
      const value = session.filterValues.get(countKey(input));
      if (value !== undefined) setFilterCount(input, value, 'exact');
      else setFilterCount(input, null, session.error || session.filterErrors.has(input.name) ? 'error' : 'pending');
    }
  }
  async function calculateColorGroup(session, group) {
    const { snap, controller } = session;
    const baseURL = new URL(snap.url);
    baseURL.searchParams.delete(group.name);
    const base = await scan(snap, baseURL.href, { signal: controller.signal });
    const baseGroup = base.filters.find(filter => filter.name === group.name);
    const hasOtherVariantFilters = Array.from(baseURL.searchParams.keys()).some(name => name.startsWith('filter.v.'));
    const plainOption = group.name.startsWith('filter.v.option.') && group.operator !== 'AND';
    const inputs = colorInputs().filter(input => input.name === group.name);
    const values = new Set(inputs.map(input => input.value));
    for (const value of values) {
      check(controller.signal);
      const serverValue = baseGroup?.values?.find(item => item.value === value);
      let count;
      // One lightweight full scan can answer ordinary Color + product-level
      // filters. Verify the raw product membership before taking this shortcut.
      if (plainOption && !hasOtherVariantFilters && serverValue &&
          bucketCount(base.rawColorProductCounts, value) === serverValue.count) {
        count = bucketCount(base.colorCounts, value);
      } else {
        // Variant price/size/availability or grouped values can change which
        // parents match. Ask Shopify for THAT exact query; never estimate it.
        const valueURL = new URL(baseURL.href);
        if (group.operator === 'AND') {
          const selected = group.values.filter(item => item.active).map(item => item.value);
          for (const selectedValue of new Set([...selected, value])) valueURL.searchParams.append(group.name, selectedValue);
        } else valueURL.searchParams.set(group.name, value);
        const valueResult = await scan(snap, valueURL.href, { signal: controller.signal });
        count = valueResult.renderedProductCards;
      }
      if (active !== session) return;
      session.filterValues.set(JSON.stringify([group.name, value]), count);
      applySession(session);
    }
  }
  async function calculateProductTypeGroup(session, group) {
    const { snap, controller } = session;
    // Product Type is an OR group. Keep every OTHER filter (including Color),
    // remove this group's selections, then count each type from the same snapshot.
    const baseURL = new URL(snap.url);
    baseURL.searchParams.delete(group.name);
    const base = await scan(snap, baseURL.href, { signal: controller.signal });
    const baseGroup = base.filters.find(filter => filter.name === group.name);
    const values = new Set(countInputs().filter(input => input.name === group.name).map(input => input.value));
    for (const value of values) {
      check(controller.signal);
      const serverValue = baseGroup?.values?.find(item => item.value === value);
      let count;
      // Reuse an existing all-pages scan only if its product.type membership
      // agrees with Shopify. Count its rendered cards, not its source products.
      if (group.operator !== 'AND' && base.productTypeDataComplete && serverValue &&
          bucketCount(base.rawProductTypeCounts, value) === serverValue.count) {
        count = bucketCount(base.productTypeCounts, value);
      } else {
        // Older/mixed assets or grouped values: query Shopify rather than guess.
        const valueURL = new URL(baseURL.href);
        if (group.operator === 'AND') {
          const selected = group.values.filter(item => item.active).map(item => item.value);
          for (const entry of new Set([...selected, value])) valueURL.searchParams.append(group.name, entry);
        } else valueURL.searchParams.set(group.name, value);
        const result = await scan(snap, valueURL.href, { signal: controller.signal });
        count = result.renderedProductCards;
      }
      if (active !== session) return;
      session.filterValues.set(JSON.stringify([group.name, value]), count);
      applySession(session);
    }
  }
  function invalidate() {
    generation += 1;
    active?.controller.abort();
    active = null;
    const snap = snapshot();
    if (!snap?.separate) return;
    setHeader('Counting...', 'pending');
    countInputs().forEach(input => setFilterCount(input, null, 'pending'));
    footer(null);
  }
  async function sync() {
    const snap = snapshot();
    if (!snap?.separate) return null;
    if (active?.snap.key === snap.key && (!active.result || Date.now() - Date.parse(active.result.generatedAt) < CACHE_MS)) {
      applySession(active);
      return active.promise;
    }
    active?.controller.abort();
    const session = {
      id: ++generation, snap, controller: new AbortController(),
      result: null, error: null, filterValues: new Map(), filterErrors: new Set(), filtersComplete: false,
    };
    active = session;
    applySession(session);
    session.promise = (async () => {
      try {
        session.result = await scan(snap, snap.url, { signal: session.controller.signal });
        if (active !== session) return null;
        applySession(session);
        const groups = session.result.filters.filter(filter =>
          (filter.color || filter.name === PRODUCT_TYPE_PARAM) && filter.values?.length);
        // A selected value can remain in the UI when Shopify omits its group
        // in an empty result. Still allow a complete, group-specific query.
        for (const input of countInputs()) {
          if (!groups.some(group => group.name === input.name)) {
            const members = countInputs().filter(item => item.name === input.name);
            groups.push({ name: input.name, color: input.name !== PRODUCT_TYPE_PARAM,
              operator: input.classList.contains('filter-and') ? 'AND' : 'OR',
              values: members.map(item => ({ value: item.value, active: item.checked })) });
          }
        }
        await Promise.all(groups.map(async group => {
          try {
            if (group.name === PRODUCT_TYPE_PARAM) await calculateProductTypeGroup(session, group);
            else await calculateColorGroup(session, group);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            session.filterErrors.add(group.name);
            console.warn('TWD filter count unavailable:', group.name, error.message);
          }
        }));
        session.filtersComplete = session.filterErrors.size === 0;
        applySession(session);
        return session.result;
      } catch (error) {
        if (error.name !== 'AbortError' && active === session) {
          session.error = error.message;
          applySession(session);
          console.warn('TWD count snapshot not applied:', error.message);
        }
        return null;
      }
    })();
    return session.promise;
  }
  async function audit({ scope = 'current', force = true } = {}) {
    if (!['current', 'collection'].includes(scope)) throw new Error('Audit scope must be current or collection.');
    const snap = snapshot({ scope });
    if (!snap) throw new Error('Open a collection page on the patched theme first.');
    const result = await scan(snap, snap.url, { force });
    console.table({
      'Shopify parent products - query': result.shopifyParentProducts,
      'Shopify parent products - entire collection': result.shopifyAllParentProducts,
      'Card candidates before is_clearance_item rule': result.cardsBeforeVisibilityRule,
      'Display cards after current theme rules': result.renderedProductCards,
      'Distinct displayed parent products': result.representedParentProducts,
      'Parent products with no displayed cards': result.hiddenParentProductsInCurrentResult,
      'Products with is_clearance_item=true variants': result.productsWithIsClearanceItemTrue ?? 'Not evaluated / incomplete',
      'Variants with is_clearance_item=true': result.isClearanceItemTrueVariants ?? 'Not evaluated / incomplete',
      'Count pages scanned': result.pagesScanned,
    });
    console.log('Exact displayed color counts:', result.colorCounts);
    console.log('Exact displayed Product Type counts:', result.productTypeDataComplete ? result.productTypeCounts : 'Type metadata unavailable in this snapshot');
    if (result.saleFamily) console.info('Sale family: display cards are restricted to custom.is_clearance_item=true.');
    if (result.variantAuditPossiblyTruncated) console.warn('At least one product reached the 250-variant Liquid limit. Display counts follow the existing renderer; the flag audit is not a complete variant inventory.');
    return result;
  }
  function status() {
    const total = active?.result?.renderedProductCards ?? null;
    const loaded = loadedCards();
    const filterCounts = {};
    for (const [key, count] of active?.filterValues || []) {
      const [name, value] = JSON.parse(key);
      if (!Object.prototype.hasOwnProperty.call(filterCounts, name)) {
        Object.defineProperty(filterCounts, name, { value: {}, enumerable: true });
      }
      Object.defineProperty(filterCounts[name], value, { value: count, enumerable: true });
    }
    const errors = Array.from(active?.filterErrors || []);
    return {
      version: ENGINE_VERSION, query: active?.snap.url,
      complete: Boolean(active?.result), totalProductCards: total,
      loadedProductCards: loaded, allResultCardsLoaded: total !== null && loaded === total,
      filterCountsComplete: Boolean(active?.filtersComplete), filterCounts,
      colorErrors: errors.filter(name => name !== PRODUCT_TYPE_PARAM),
      productTypeErrors: errors.filter(name => name === PRODUCT_TYPE_PARAM),
      filterErrors: errors, error: active?.error, result: active?.result,
    };
  }
  window.TWDCollectionCounts = { version: ENGINE_VERSION, sync, invalidate, audit, status };
  window.twdSyncSeparateColorDisplayCounts = sync;
  window.twdInvalidateCollectionCounts = invalidate;
  window.twdAuditCollectionCounts = audit;
  window.twdGetCollectionCountStatus = window.TWDCollectionCounts.status;
  document.addEventListener('eurus:collection:reloaded', () => requestAnimationFrame(sync));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(sync), { once: true });
  else requestAnimationFrame(sync);
})();
